import { useState, useEffect, useCallback, useMemo } from "react";
import { firebaseDB } from "firebase/client";
import { normalizeText } from "helpers";
import { mergeRoles } from "model/options";

/**
 * Operadores que Firestore no sabe resolver y que se aplican en memoria sobre
 * el resultado de la query. Los listados de administración ya se traen enteros
 * (filtran por `actived` y ordenan, nada más), así que filtrar acá no agrega
 * lecturas: al contrario, tipear en un filtro deja de disparar una query nueva
 * por cada letra, y se evitan los índices compuestos que pediría Firestore al
 * combinar varios campos.
 *
 * - `contains`: búsqueda por texto. Firestore no tiene un "contiene": el `>=`
 *   que usaban los toolbars devolvía todo lo que ordenara después de lo
 *   tipeado y además distinguía mayúsculas, así que "pablo" se salteaba a
 *   "Pablo Cafferata" y se traía a los que vinieran después en el abecedario.
 * - `equals`: igualdad exacta pero comparando como texto normalizado (los años
 *   se guardan como string).
 * - `role-checked`: el rol de un usuario. `users.roles` es un array de objetos
 *   `{value,label,checked}` (y en los registros viejos sólo `{checked}`), así
 *   que el `array-contains` con el value del rol no matcheaba nunca.
 */
const clientSideOperators = {
  contains: (fieldValue, value) =>
    normalizeText(fieldValue).includes(normalizeText(value)),

  equals: (fieldValue, value) => normalizeText(fieldValue) === normalizeText(value),

  "role-checked": (fieldValue, value) =>
    mergeRoles(fieldValue).some((role) => role.value === value && role.checked),
};

export default function useCrudFlow(table, sortBy) {
  const [loading, setLoading] = useState(false);

  const [waiting, setWaiting] = useState(false);

  const [items, setItems] = useState([]);

  const [onlyActived, setOnlyActived] = useState(true);

  const [errors, setErrors] = useState([]);

  const [filteringQueries, setFilteringQueries] = useState([]);

  const [clientFilters, setClientFilters] = useState([]);

  const [sortingQuery, setSortingQuery] = useState({
    field: sortBy,
    direction: "asc",
  });

  const deleteItem = (id) => {
    setWaiting(true);

    let promise = new Promise((resolve, reject) => {
      firebaseDB
        .collection(table)
        .doc(id)
        .delete()
        .then(function () {
          resolve(true);
        })
        .catch(function (error) {
          setErrors(error);
          reject(error);
        })
        .finally(() => {
          setWaiting(false);
        });
    });

    return promise;
  };

  const switchActivationItem = (id, actived) => {
    return updateItem(id, { actived });
  };

  const updateItem = (id, fields) => {
    let promise = new Promise((resolve, reject) => {
      firebaseDB
        .collection(table)
        .doc(id)
        .set(fields, { merge: true })
        .then(function () {
          resolve(true);
        })
        .catch(function (error) {
          setErrors(error);
          reject(error);
          console.error(error);
        })
        .finally(() => {
          setWaiting(false);
        });
    });

    return promise;
  };

  const createItem = (fields) => {
    setWaiting(true);

    let promise = new Promise((resolve, reject) => {
      firebaseDB
        .collection(table)
        .doc()
        .set(fields)
        .then(function (doc) {
          resolve(doc);
        })
        .catch(function (error) {
          setErrors(error);
          reject(error);
          console.log(error);
        })
        .finally(() => {
          setWaiting(false);
        });
    });

    return promise;
  };

  // Un filtro se resuelve en Firestore o en memoria según el operador. El
  // campo se saca de las dos listas antes de volver a agregarlo, así un mismo
  // campo no queda filtrado dos veces si alguna vez cambia de operador.
  const updateFiltering = (field, operator, value) => {
    const isClientSide = !!clientSideOperators[operator];

    const withoutField = (queries) =>
      queries.filter((item) => item.field !== field);

    const add = (queries) =>
      value
        ? [...withoutField(queries), { field, operator, value }]
        : withoutField(queries);

    setFilteringQueries((queries) =>
      isClientSide ? withoutField(queries) : add(queries)
    );

    setClientFilters((queries) =>
      isClientSide ? add(queries) : withoutField(queries)
    );
  };

  const updateSorting = (field, direction) => {
    setSortingQuery({ field: field, direction: direction });
  };

  const getItems = useCallback(() => {
    setLoading(true);

    var query = firebaseDB.collection(table);

    let filtering = filteringQueries;

    if (onlyActived) {
      filtering = [{ field: "actived", operator: "==", value: true }].concat(
        filtering
      );
    }

    let orderingQueries = [sortingQuery];

    filtering.forEach((item) => {
      query = query.where(item.field, item.operator, item.value);

      if (
        sortingQuery.field !== item.field &&
        (item.operator === "<" ||
          item.operator === "<=" ||
          item.operator === ">" ||
          item.operator === ">=")
      ) {
        orderingQueries = [{ field: item.field, direction: "asc" }].concat(
          orderingQueries
        );
      }
    });

    orderingQueries.forEach((item) => {
      query = query.orderBy(item.field);
    });

    query
      .get()
      .then(({ docs }) => {
        const result = docs.map((doc) => {
          return { ...doc.data(), id: doc.id };
        });

        setItems(result);
      })
      .catch(function (error) {
        console.error(error);
        setErrors(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [filteringQueries, onlyActived, sortingQuery, table]);

  useEffect(() => {
    getItems();
  }, [getItems]);

  const filteredItems = useMemo(
    () =>
      clientFilters.reduce(
        (result, { field, operator, value }) =>
          result.filter((item) =>
            clientSideOperators[operator](item[field], value)
          ),
        items
      ),
    [clientFilters, items]
  );

  return {
    loading,
    waiting,
    errors,
    items: filteredItems,
    counter: filteredItems.length,
    getItems,
    updateFiltering,
    updateSorting,
    updateItem,
    createItem,
    deleteItem,
    switchActivationItem,
    setOnlyActived,
  };
}
